<template>
  <div class="page-container reject-reason-list">
    <div class="page-title-container">
      <h1 class="page-title">Auditoría</h1>
    </div>

    <div class="table-container">
      <div class="table-header">
        <div
          class="d-flex flex-column flex-md-row justify-content-between align-items-start position-relative"
        >
          <div class="w-full w-md-auto md-flex-1">
            <div
              class="d-flex justify-content-between align-items-center justify-content-lg-start"
            >
              <button
                type="button"
                class="filter d-flex align-items-center justify-content-between border-0"
                @click="showFilters = !showFilters"
              >
                <i class="ri-filter-3-line text-dark font-weight-bold"></i>
                <div>Filtrar</div>
              </button>

              <div class="d-flex d-md-none">
                <button
                  type="button"
                  class="button-tooltip text-dark font-weight-bold mr-0"
                  v-b-tooltip.hover
                  title="Recargar"
                  @click="loadData()"
                >
                  <i class="ri-loop-right-line"></i>
                </button>

                <button
                  type="button"
                  class="button-tooltip text-dark font-weight-bold mr-0"
                  v-b-tooltip.hover
                  title="Descargar"
                  @click="downloadReport()"
                >
                  <i class="ri-download-2-line"></i>
                </button>
              </div>
            </div>
          </div>

          <section
            class="w-full w-md-auto md-flex-1 d-flex justify-content-end align-items-center mt-2 mt-md-0"
          >
            <div class="d-none d-md-flex">
              <button
                type="button"
                class="button-tooltip text-dark font-weight-bold"
                v-b-tooltip.hover
                title="Recargar"
                @click="loadData()"
              >
                <i class="ri-loop-right-line"></i>
              </button>

              <button
                  type="button"
                  class="button-tooltip text-dark font-weight-bold ml-0"
                  v-b-tooltip.hover
                  title="Descargar"
                  @click="downloadReport()"
                >
                  <i class="ri-download-2-line"></i>
                </button>
            </div>

            <div class="w-full w-md-auto d-flex flex-column flex-md-row">
              <SearchBar
                placeholder="Buscar por usuario, descripción"
                @search="onSearch"
                @input="search = $event"
                class="flex-1"
              />
            </div>
          </section>

          <filter-menu
            :show="showFilters"
            @hide="showFilters = false"
            :options="filterOptions"
            :selectedOptionFilter="filters"
            @setFilters="setFilters"
          ></filter-menu>
        </div>

        <!-- Filtro por registro concreto (DEF-ADM-023b §6.2). No está en la
             barra de filtros: solo se llega desde el modal, y se quita aquí. -->
        <div class="w-100 d-flex flex-wrap align-items-center mt-2" v-if="entityFilter">
          <span class="filter-item">
            {{ getSectionLabel(entityFilter.section) }} #{{ entityFilter.entityId }}
            <button
              type="button"
              class="text-white p-0 bg-transparent border-0 ml-2"
              @click="clearEntityFilter()"
            >
              <i class="ri-close-circle-line"></i>
            </button>
          </span>
        </div>

        <div
          class="w-100 d-flex flex-wrap align-items-center mt-2"
          v-if="showFiltersList"
        >
          <span
            v-for="(val, name) in filterList"
            v-bind:key="name"
            class="filter-item"
          >
            {{ getFilterName(name, val) }}
            <button
              type="button"
              class="text-white p-0 bg-transparent border-0 ml-2"
              @click="deleteFilter(name)"
            >
              <i class="ri-close-circle-line"></i>
            </button>
          </span>
        </div>
      </div>

      <div class="table-responsive mb-0">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th>Acción</th>
              <th>Sección</th>
              <th>Fecha y hora</th>
              <th>Usuario</th>
              <th>Descripción</th>
              <th class="pr-md-4">Opciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="o in list" :key="o.id" class="row-clickable" @click="openDetail(o)">
              <td class="cell-center pl-3">
                <span :class="`badge ${getAction(o.action).color}`">{{
                    getAction(o.action).label
                  }}</span>
              </td>
              <td class="cell-center px-4">{{ getSectionLabel(o.section) }}</td>
              <td class="cell-center pl-3" v-local-date:hour="o.created_at"></td>
              <td class="cell-center px-4">{{ trimEmail(o.actor) }}</td>
              <td class="pl-5">{{ o.description }}</td>
              <td class="cell-center pr-md-3">
                <button type="button" class="link-button" @click.stop="openDetail(o)">
                  Ver detalle
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="table-footer d-flex justify-content-end align-items-center">
        <pagination
          :total="total"
          :page="page"
          :page-size="pageSize"
          :pages="pages"
          :changePage="changePage"
          :changePageSize="changePageSize"
          v-if="list.length"
        />

        <div class="table-no-results" v-else>
          <p class="m-0 px-3">No se encontraron resultados.</p>
        </div>
      </div>
    </div>

    <!-- Detalle del registro con la tabla de cambios (DEF-ADM-023 §4.2) -->
    <b-modal v-model="detail.show" title="Detalle del registro" size="lg" hide-footer>
      <div v-if="detail.entry">
        <div class="detail-head">
          <div class="detail-item">
            <span class="detail-label">Acción</span>
            <span :class="`badge ${getAction(detail.entry.action).color}`">
              {{ getAction(detail.entry.action).label }}
            </span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Sección</span>
            <span>{{ getSectionLabel(detail.entry.section) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Usuario</span>
            <span>{{ trimEmail(detail.entry.actor) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Fecha y hora</span>
            <span v-local-date:hour="detail.entry.created_at"></span>
          </div>
        </div>

        <p class="detail-description">{{ detail.entry.description }}</p>

        <p class="detail-history" v-if="detail.entry.entity_id">
          <button type="button" class="link-button" @click="filterByEntity(detail.entry)">
            <i class="ri-history-line align-middle mr-1"></i>Ver todos los cambios de este registro
          </button>
        </p>

        <div v-if="detail.loading" class="detail-empty">Cargando cambios…</div>

        <div v-else-if="changeRows.length" class="table-responsive mb-0">
          <table class="custom-table w-100">
            <thead>
              <tr>
                <th class="text-left">Campo</th>
                <th class="text-left">Valor anterior</th>
                <th class="text-left">Valor nuevo</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in changeRows" :key="row.field">
                <td class="font-weight-bold">{{ row.label }}</td>
                <td class="text-muted">{{ row.before }}</td>
                <td>{{ row.after }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="detail-empty">
          Detalle no disponible para registros anteriores.
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  Alert,
  Constants,
  FilterMenu,
  FilterMixin,
  PaginationMixin,
  SearchBar,
  Util,
} from "wize-admin";

import { Constants as LocalConstants } from "@/core/utils";
import { AuditLogService } from "@/core/services";

export default {
  components: {
    SearchBar,
    FilterMenu,
  },
  data() {
    return {
      list: [],
      selectedRejectReason: null,
      tooltipType: null,
      rejectReasons: [],
      selectedPaymentId: null,
      detail: { show: false, loading: false, entry: null },
      // { section, entityId } cuando se entra desde "ver todos los cambios de
      // este registro"; null el resto del tiempo.
      entityFilter: null,
      filterOptions: [
        {
          label: "Acción",
          key: "action",
          type: Constants.TypeFilters.SINGLE,
          // Los seis tipos del DEF. Las acciones de contraseña se ven en la
          // tabla pero no se ofrecen como filtro: son casos puntuales.
          options: [
            LocalConstants.AuditLogAction.CREATE,
            LocalConstants.AuditLogAction.UPDATE,
            LocalConstants.AuditLogAction.DELETE,
            LocalConstants.AuditLogAction.STATUS_CHANGE,
            LocalConstants.AuditLogAction.ASSIGN,
            LocalConstants.AuditLogAction.LOGIN,
          ],
        },
        {
          label: "Sección",
          key: "section",
          type: Constants.TypeFilters.SINGLE,
          // Sale del mismo mapa que usa la columna, así que ya no se puede
          // desfasar del backend. Antes ofrecía 13 de las 26 secciones reales
          // e incluía LOGIN, que no existe como sección (los inicios de sesión
          // se guardan en AUTH) y por eso no devolvía nada.
          options: Object.entries(LocalConstants.AuditLogSection)
            .map(([key, label]) => ({ key, label }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        },
        {
          label: "Fecha",
          key: "date",
          type: Constants.TypeFilters.DATE,
          isRange: true,
        },
      ],
    };
  },
  computed: {
    // Filas de la tabla de cambios: el diff del backend traducido a campos
    // legibles. null se muestra como guión.
    changeRows() {
      const changes = this.detail.entry && this.detail.entry.changes;
      if (!changes) return [];
      return Object.keys(changes)
        .sort()
        .map((field) => ({
          field,
          label: LocalConstants.AuditLogField[field] || field,
          before: this.formatValue(changes[field].before),
          after: this.formatValue(changes[field].after),
        }));
    },
  },
  mixins: [FilterMixin, PaginationMixin],
  methods: {
    async loadData() {
      try {
        await this.$store.dispatch("app/loading", true);

        const params = {
          page: this.page - 1,
          size: this.pageSize,
        };
        const data = { search: this.search, ...this.filters };
        if (this.entityFilter) {
          data.section = this.entityFilter.section;
          data.entity_id = this.entityFilter.entityId;
        }
        if (data.date) {
          data.date_from = data.date.start ? new Date(data.date.start).toISOString().slice(0, 10) : undefined;
          data.date_to = data.date.end ? new Date(data.date.end).toISOString().slice(0, 10) : undefined;
          delete data.date;
        }
        const response = await AuditLogService.list(data, params);

        this.list = response.payload.items.map((item) => {
          return {
            ...item,
            showMobileData: false,
          };
        });

        this.total = response.payload.metadata.totalElements;
        this.pages = response.payload.metadata.totalPages;
      } catch (e) {
        console.error(e);
        Alert.error(e.errors.message);
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
    getAction(key) {
      return Util.searchValue(key, LocalConstants.AuditLogAction);
    },
    getSectionLabel(section) {
      // Las secciones en minúscula son de registros anteriores a la
      // normalización (migración 000058); se resuelven igual.
      const key = (section || '').toUpperCase()
      return LocalConstants.AuditLogSection[key] || section
    },
    // Valor de una celda del diff. null/vacío → guión, booleanos legibles.
    formatValue(value) {
      if (value === null || value === undefined || value === '') return '—'
      if (typeof value === 'boolean') return value ? 'Sí' : 'No'
      if (typeof value === 'object') return JSON.stringify(value)
      return String(value)
    },
    async openDetail(row) {
      // Se abre ya con lo que trae el listado y el diff se pide aparte, para
      // que el modal no se quede en blanco mientras carga.
      this.detail = { show: true, loading: true, entry: { ...row, changes: null } }
      try {
        const response = await AuditLogService.get(row.id)
        this.detail.entry = response.payload
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'No se pudo cargar el detalle')
      } finally {
        this.detail.loading = false
      }
    },
    // Enlace del modal: deja la lista mostrando solo el historial de esa fila.
    async filterByEntity(entry) {
      this.entityFilter = { section: entry.section, entityId: entry.entity_id };
      this.detail.show = false;
      this.page = 1;
      await this.loadData();
    },
    async clearEntityFilter() {
      this.entityFilter = null;
      this.page = 1;
      await this.loadData();
    },
    async downloadReport() {
      try {
        await this.$store.dispatch("app/loading", true);
        const data = { search: this.search, ...this.filters };
        if (this.entityFilter) {
          data.section = this.entityFilter.section;
          data.entity_id = this.entityFilter.entityId;
        }
        const response = await AuditLogService.download(data);
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Auditoria_Travelcab_${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        Alert.error(error);
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
    trimEmail(val) {
      return Util.trimEmail(val);
    }
  },
  async mounted() {
    await this.loadData(this.page, this.pageSize);
  },
};
</script>

<style lang="stylus" scoped>
.row-clickable
  cursor pointer
.link-button
  background none
  border none
  padding 0
  color var(--color-primary)
  font-size 0.82rem
  cursor pointer
  &:hover
    text-decoration underline
.detail-head
  display flex
  flex-wrap wrap
  gap 24px
  padding-bottom 12px
  border-bottom 0.3px solid #e5e7eb
.detail-item
  display flex
  flex-direction column
  gap 4px
.detail-label
  font-size 11px
  text-transform uppercase
  letter-spacing .04em
  color #6c757d
.detail-history
  margin -8px 0 16px
.detail-description
  margin 12px 0 16px
  font-size 14px
  color #495057
.detail-empty
  padding 24px 8px
  text-align center
  color #6c757d
  font-size 14px
</style>
